
import React from "react";
import { Analytics } from "@vercel/analytics/react";
import "./App.css";

import Header from "./components/Header";
import Hours from "./components/Hours";
import Footer from "./components/Footer";

function App() {
  const BUSINESS_NAME = "Big Pace Barber Shop and Beauty Salon 2";
  const PHONE_E164 = "+17622154738";
  const PHONE_DISPLAY = "(762) 215-4738";

  const STREET = "3355 Deans Bridge Rd Ste D";
  const CITY = "Augusta";
  const STATE = "GA";
  const ZIP = "30906";

  const FACEBOOK_URL =
    "https://www.facebook.com/Big-Pace-Barber-Shop-and-Beauty-Salon-2-103462851469536/?ref=page_internal";

  const mapsQuery = encodeURIComponent(
    `${STREET}, ${CITY}, ${STATE} ${ZIP}`
  );

  const mapsUrl =
    `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;

  const barberServices = [
    "Adult Haircuts",
    "Kids' Haircuts",
    "Beard Trims",
    "Straight Razor Service",
    "Tape with Beard",
    "Regular Tape",
  ];

  const beautyServices = [
    "Beauty Salon Services",
    "Dreadlocks",
    "Retwists",
    "Sew-Ins",
    "Relaxers",
    "Additional Hair Services",
  ];

  const serviceHighlights = [
    "Haircuts & Beard Trims",
    "Dreadlocks & Retwists",
    "Salon Services",
  ];

  return (
    <div className="App">
      <Header />

      <main id="main-content">
        {/* HERO */}
        <section
          className="hero-section container"
          aria-labelledby="main-heading"
        >
          <div className="hero-content">
            <p className="hero-eyebrow">
              AUGUSTA, GEORGIA · DEANS BRIDGE ROAD
            </p>

            <h1 id="main-heading" className="site-title">
              Big Pace Barber Shop
              <span className="site-title-secondary">
                &amp; Beauty Salon 2
              </span>
            </h1>

            <p className="hero-tagline">
              Haircuts, beard trims, dreadlocks, retwists,
              sew-ins, and beauty services — all in one
              Augusta shop.
            </p>

            <p className="walk-ins hero-walk-ins">
              Walk-ins Always Welcome
            </p>

            <div
              className="hero-highlights"
              aria-label="Featured services"
            >
              {serviceHighlights.map((service) => (
                <span
                  key={service}
                  className="hero-highlight"
                >
                  {service}
                </span>
              ))}
            </div>

            <div className="cta-row hero-actions">
              <a
                className="btn phone"
                href={`tel:${PHONE_E164}`}
                aria-label={`Call ${BUSINESS_NAME} at ${PHONE_DISPLAY}`}
              >
                <span aria-hidden="true">☎</span>
                Call {PHONE_DISPLAY}
              </a>

              <a
                className="btn directions"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get directions to Big Pace Barber Shop and Beauty Salon 2"
              >
                <span aria-hidden="true">⌖</span>
                Get Directions
              </a>
            </div>

            <address className="hero-address">
              {STREET}
              <br />
              {CITY}, {STATE} {ZIP}
            </address>

            <a
              className="hero-services-link text-link"
              href="#services-heading"
            >
              Explore Our Services ↓
            </a>
          </div>

          <div className="hero-image-wrapper">
            <img
              src="/storefront.jpg"
              alt="Storefront of Big Pace Barber Shop and Beauty Salon 2 on Deans Bridge Road in Augusta, Georgia"
              className="responsive-img hero-image"
              loading="eager"
              fetchPriority="high"
              width="1600"
              height="900"
            />
          </div>
        </section>

        {/* SHOP HOURS */}
        <section
          className="hours-section container"
          aria-labelledby="hours-heading"
        >
          <div className="section-heading">
            <p className="section-eyebrow">
              PLAN YOUR VISIT
            </p>

            <h2 id="hours-heading">Shop Hours</h2>

            <p className="section-intro">
              Planning your next haircut or salon visit?
              Check our shop hours before stopping by.
            </p>
          </div>

          <Hours />

          <div className="hours-actions">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              Get directions to the shop →
            </a>
          </div>
        </section>

        {/* SERVICES */}
        <section
          className="services-section container"
          aria-labelledby="services-heading"
        >
          <div className="section-heading">
            <p className="section-eyebrow">WHAT WE DO</p>

            <h2 id="services-heading">
              Barber &amp; Beauty Services in Augusta, GA
            </h2>

            <p className="section-intro">
              From everyday haircuts and beard grooming
              to dreadlocks, retwists, sew-ins, and beauty
              services, Big Pace serves customers right
              here in Augusta.
            </p>
          </div>

          <div className="service-groups">
            <article className="service-card">
              <div
                className="service-icon"
                aria-hidden="true"
              >
                💈
              </div>

              <h3>Barber Services</h3>

              <p className="service-description">
                Haircuts, beard grooming, and traditional
                barber services at our Augusta location.
              </p>

              <ul className="service-list">
                {barberServices.map((service) => (
                  <li key={service}>{service}</li>
                ))}
              </ul>
            </article>

            <article className="service-card">
              <div
                className="service-icon"
                aria-hidden="true"
              >
                ✂️
              </div>

              <h3>Hair &amp; Beauty</h3>

              <p className="service-description">
                Hair care, styling, and salon services
                for different styles and preferences.
              </p>

              <ul className="service-list">
                {beautyServices.map((service) => (
                  <li key={service}>{service}</li>
                ))}
              </ul>
            </article>
          </div>

          <div className="services-callout">
            <strong>
              Have a question about a service?
            </strong>

            <p>
              Call Big Pace to ask about availability,
              pricing, or the service you need.
            </p>

            <a
              className="text-link"
              href={`tel:${PHONE_E164}`}
            >
              Call {PHONE_DISPLAY} →
            </a>
          </div>
        </section>

        {/* PRICE LIST */}
        <section
          className="pricing-section container"
          aria-labelledby="pricing-heading"
        >
          <div className="section-heading">
            <p className="section-eyebrow">
              SERVICE PRICING
            </p>

            <h2 id="pricing-heading">
              Barber &amp; Beauty Service Prices
            </h2>

            <p className="section-intro">
              Browse our posted price list below.
              Please call the shop to confirm current
              pricing or ask about a specific service.
            </p>
          </div>

          <div className="price-list-wrapper">
            <img
              src="/updatedprice.png"
              alt="Posted service price list for Big Pace Barber Shop and Beauty Salon 2"
              className="responsive-img price-list-image"
              loading="lazy"
              width="1200"
              height="1600"
            />
          </div>

          <div className="pricing-actions">
            <a
              className="text-link"
              href={`tel:${PHONE_E164}`}
            >
              Questions about pricing? Call the shop →
            </a>
          </div>
        </section>

        {/* WHY BIG PACE */}
        <section
          className="trust-section"
          aria-labelledby="why-big-pace-heading"
        >
          <div className="container">
            <div className="section-heading">
              <p className="section-eyebrow">
                YOUR NEIGHBORHOOD SHOP
              </p>

              <h2 id="why-big-pace-heading">
                Come See Us at Big Pace
              </h2>

              <p className="section-intro">
                Find barbering, grooming, and beauty
                services at our Deans Bridge Road
                location in Augusta, Georgia.
              </p>
            </div>

            <div className="trust-grid">
              <article className="trust-item">
                <span
                  className="trust-icon"
                  aria-hidden="true"
                >
                  🚶
                </span>

                <h3>Walk Right In</h3>

                <p>
                  No complicated online process.
                  Walk-ins are always welcome.
                </p>
              </article>

              <article className="trust-item">
                <span
                  className="trust-icon"
                  aria-hidden="true"
                >
                  💈
                </span>

                <h3>Barber &amp; Beauty</h3>

                <p>
                  Barbering, grooming, hair care,
                  and beauty services available
                  in one location.
                </p>
              </article>

              <article className="trust-item">
                <span
                  className="trust-icon"
                  aria-hidden="true"
                >
                  📍
                </span>

                <h3>Augusta Location</h3>

                <p>
                  Find us at {STREET} in Augusta,
                  Georgia.
                </p>
              </article>
            </div>

            <div className="trust-actions">
              <a
                className="btn directions"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Directions
              </a>

              <a
                className="btn secondary"
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit Us on Facebook
              </a>
            </div>
          </div>
        </section>

        {/* NOW HIRING */}
        <section
          className="hiring-section container"
          aria-labelledby="hiring-heading"
        >
          <div className="hiring-card">
            <div>
              <p className="section-eyebrow">
                JOIN THE TEAM
              </p>

              <h2 id="hiring-heading">
                Now Hiring Licensed Barbers
              </h2>

              <p>
                Interested in working at Big Pace?
                Call the shop for details about
                current opportunities.
              </p>
            </div>

            <a
              className="btn phone"
              href={`tel:${PHONE_E164}`}
              aria-label={`Call ${BUSINESS_NAME} about barber opportunities`}
            >
              Call {PHONE_DISPLAY}
            </a>
          </div>
        </section>

        {/* FINAL CALL TO ACTION */}
        <section
          className="visit-section"
          aria-labelledby="visit-heading"
        >
          <div className="container visit-content">
            <p className="section-eyebrow">
              READY FOR A FRESH LOOK?
            </p>

            <h2 id="visit-heading">
              Visit Big Pace Barber Shop in Augusta
            </h2>

            <p>
              Whether you're stopping in for a haircut,
              beard trim, retwist, or beauty service,
              we're ready to welcome you.
            </p>

            <div className="cta-row">
              <a
                className="btn phone"
                href={`tel:${PHONE_E164}`}
                aria-label={`Call ${BUSINESS_NAME} at ${PHONE_DISPLAY}`}
              >
                Call {PHONE_DISPLAY}
              </a>

              <a
                className="btn directions"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Directions
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <Analytics />
    </div>
  );
}

export default App;
